/**
 * Picks a readable table and headline numbers out of a tool result. Pure.
 */
import { humanize } from './agentContextMap';

const ARRAY_KEYS = [
  'candidates',
  'arrivals',
  'departures',
  'overdueCheckouts',
  'bookings',
  'items',
  'groups',
  'lines',
  'payments',
  'actions',
  'availability',
  'services',
  'courses',
  'roomTypes',
  'rooms',
  'beds',
  'skills',
  'data',
  'rows',
];

const COLUMN_ORDER = [
  'bookingNumber',
  'guest',
  'name',
  'service',
  'description',
  'roomType',
  'summary',
  'status',
  'outcome',
  'eligible',
  'readyForCheckIn',
  'checkIn',
  'checkOut',
  'accommodation',
  'quantity',
  'count',
  'unitPrice',
  'stockValue',
  'gstRatePercent',
  'gst',
  'total',
  'paid',
  'balanceDue',
  'amount',
  'method',
  'blockers',
  'webCheckin',
];

const HIDDEN = new Set(['bookingId', 'itemId', 'serviceId', 'roomTypeId', 'courseId', 'actionId', 'id', 'userId', 'roomId', 'bedId', 'paymentId', 'taxes', 'notes', 'services', 'phone', 'email', 'paymentLink']);

const MONEY = /^(total|paid|refunded|balanceDue|amount|stockValue|unitPrice|price|fee|basePrice|nightlyBasePrice|gst|gstOnStockValue|outstanding|totalOutstanding|totalStockValue|sum\.(stockValue|gst|amount))$/;

const isRecord = (value) => !!value && typeof value === 'object' && !Array.isArray(value);
const isPrimitive = (value) => value === null || ['string', 'number', 'boolean'].includes(typeof value);

export function formatInr(value) {
  const rounded = Math.round(Number(value) * 100) / 100;
  return `Rs ${rounded.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

export function formatCell(value, column) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length ? value.map((entry) => (isPrimitive(entry) ? String(entry) : JSON.stringify(entry))).join('; ') : '—';
  if (typeof value === 'number' && MONEY.test(column)) return formatInr(value);
  if (typeof value === 'number') return value.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  if (isRecord(value)) return JSON.stringify(value);
  return String(value);
}

function flattenGroups(groups) {
  return groups.map((group) => {
    const row = { ...(group.key || {}), count: group.count };
    for (const [field, value] of Object.entries(group.sum || {})) row[field] = value;
    for (const [field, value] of Object.entries(group.avg || {})) row[`avg ${field}`] = value;
    return row;
  });
}

function toTable(key, rows) {
  const seen = [];
  for (const row of rows.slice(0, 25)) {
    for (const [field, value] of Object.entries(row)) {
      const usable = isPrimitive(value) || (Array.isArray(value) && value.every(isPrimitive));
      if (usable && !HIDDEN.has(field) && !seen.includes(field)) seen.push(field);
    }
  }
  const rank = (field) => {
    const index = COLUMN_ORDER.indexOf(field);
    return index === -1 ? COLUMN_ORDER.length + seen.indexOf(field) : index;
  };
  const columns = [...seen].sort((a, b) => rank(a) - rank(b)).slice(0, 7);
  return { key, title: humanize(key), columns, rows, total: rows.length };
}

export function extractTable(result) {
  if (Array.isArray(result)) {
    return result.length && isRecord(result[0]) ? toTable('results', result) : null;
  }
  if (!isRecord(result)) return null;
  for (const key of ARRAY_KEYS) {
    const value = result[key];
    if (Array.isArray(value) && value.length && isRecord(value[0])) {
      return toTable(key, key === 'groups' ? flattenGroups(value) : value);
    }
  }
  for (const [key, value] of Object.entries(result)) {
    if (Array.isArray(value) && value.length && isRecord(value[0])) return toTable(key, value);
  }
  return null;
}

export function extractStats(result) {
  if (!isRecord(result)) return [];
  const source = result.summary || result.counts || result.folio || (result.totals && result.totals.sum) || null;
  const stats = [];
  if (isRecord(source)) {
    for (const [field, value] of Object.entries(source)) {
      if (typeof value === 'number' || typeof value === 'boolean') stats.push({ label: humanize(field), value: formatCell(value, field) });
    }
  }
  if (isRecord(result.money)) {
    for (const [field, value] of Object.entries(result.money)) stats.push({ label: humanize(field), value: formatCell(value, 'amount') });
  }
  if (result.totals && typeof result.totals.count === 'number') stats.unshift({ label: 'Rows', value: String(result.totals.count) });
  return stats.slice(0, 8);
}
